import { useState } from "react";
import { X, Lock, Loader2 } from "lucide-react";
import { WhatsAppIcon } from "./BrandIcons";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const UF_OPTIONS = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA",
  "MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN",
  "RS","RO","RR","SC","SP","SE","TO",
];

function maskPhone(value: string) {
  const d = value.replace(/\D/g, "").substring(0, 11);
  const m = d.match(/^(\d{0,2})(\d{0,5})(\d{0,4})$/) || [];
  if (!m[2]) return m[1] || "";
  return `(${m[1]}) ${m[2]}${m[3] ? `-${m[3]}` : ""}`;
}

function maskCnpj(value: string) {
  const d = value.replace(/\D/g, "").substring(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}

function validateCnpj(cnpj: string): boolean {
  const digits = cnpj.replace(/\D/g, "");
  if (digits.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(digits)) return false;
  const calc = (len: number) => {
    let sum = 0;
    let pos = len - 7;
    for (let i = 0; i < len; i++) {
      sum += parseInt(digits[i]) * pos--;
      if (pos < 2) pos = 9;
    }
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  if (calc(12) !== parseInt(digits[12])) return false;
  if (calc(13) !== parseInt(digits[13])) return false;
  return true;
}

interface FormState {
  nome: string;
  whatsapp: string;
  cnpj: string;
  cidade: string;
  uf: string;
  lgpd: boolean;
}

interface LeadFormProps {
  onSubmit: (data: FormState) => void;
  layout?: "sidebar" | "card";
}

export function LeadForm({ onSubmit, layout = "sidebar" }: LeadFormProps) {
  const [form, setForm] = useState<FormState>({ nome: "", whatsapp: "", cnpj: "", cidade: "", uf: "", lgpd: false });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const inputClass = "w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-slate-700 focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red transition-colors text-sm";
  const labelClass = "block text-xs font-bold text-slate-700 uppercase mb-1 tracking-wide";
  const errorClass = "text-xs text-red-500 mt-1";

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.nome.trim() || form.nome.trim().length < 2) errs.nome = "Nome obrigatório (mín. 2 caracteres)";
    const phone = form.whatsapp.replace(/\D/g, "");
    if (phone.length < 10) errs.whatsapp = "WhatsApp inválido";
    const cnpjDigits = form.cnpj.replace(/\D/g, "");
    if (!cnpjDigits) {
      errs.cnpj = "O CNPJ é obrigatório";
    } else if (!validateCnpj(form.cnpj)) {
      errs.cnpj = "CNPJ inválido";
    }
    if (!form.cidade.trim()) errs.cidade = "Cidade obrigatória";
    if (!form.uf) errs.uf = "Selecione o estado";
    if (!form.lgpd) errs.lgpd = "Você precisa aceitar a política de privacidade";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div>
        <label htmlFor="lead-nome" className={labelClass}>Nome Completo</label>
        <input id="lead-nome" name="nome" type="text" autoComplete="name" maxLength={100} className={`${inputClass} ${errors.nome ? "border-red-400" : ""}`} placeholder="Seu nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
        {errors.nome && <p className={errorClass}>{errors.nome}</p>}
      </div>

      <div className={layout === "card" ? "grid grid-cols-2 gap-4" : "grid grid-cols-2 gap-3"}>
        <div>
          <label htmlFor="lead-whatsapp" className={labelClass}>WhatsApp</label>
          <input id="lead-whatsapp" name="whatsapp" type="tel" autoComplete="tel" maxLength={15} className={`${inputClass} ${errors.whatsapp ? "border-red-400" : ""}`} placeholder="(00) 00000-0000" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: maskPhone(e.target.value) })} />
          {errors.whatsapp && <p className={errorClass}>{errors.whatsapp}</p>}
        </div>
        <div>
          <label htmlFor="lead-cnpj" className={labelClass}>CNPJ</label>
          <input id="lead-cnpj" name="cnpj" type="text" maxLength={18} className={`${inputClass} ${errors.cnpj ? "border-red-400" : ""}`} placeholder="00.000.000/0000-00" value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: maskCnpj(e.target.value) })} />
          {errors.cnpj && <p className={errorClass}>{errors.cnpj}</p>}
        </div>
      </div>

      <div className={layout === "card" ? "grid grid-cols-3 gap-4" : "grid grid-cols-3 gap-3"}>
        <div className="col-span-2">
          <label htmlFor="lead-cidade" className={labelClass}>Cidade</label>
          <input id="lead-cidade" name="cidade" type="text" autoComplete="address-level2" maxLength={80} className={`${inputClass} ${errors.cidade ? "border-red-400" : ""}`} placeholder="Sua cidade" value={form.cidade} onChange={(e) => setForm({ ...form, cidade: e.target.value })} />
          {errors.cidade && <p className={errorClass}>{errors.cidade}</p>}
        </div>
        <div>
          <label htmlFor="lead-uf" className={labelClass}>Estado</label>
          <select id="lead-uf" name="uf" autoComplete="address-level1" className={`${inputClass} ${errors.uf ? "border-red-400" : ""}`} value={form.uf} onChange={(e) => setForm({ ...form, uf: e.target.value })}>
            <option value="">UF</option>
            {UF_OPTIONS.map((uf) => (<option key={uf} value={uf}>{uf}</option>))}
          </select>
          {errors.uf && <p className={errorClass}>{errors.uf}</p>}
        </div>
      </div>

      <div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.lgpd}
            onChange={(e) => setForm({ ...form, lgpd: e.target.checked })}
            className="mt-0.5 w-4 h-4 rounded border-slate-300 text-brand-red focus:ring-brand-red accent-brand-red"
          />
          <span className="text-xs text-slate-600 leading-relaxed">
            Declaro que li e concordo com a utilização dos meus dados pessoais conforme a política de privacidade da Toraflex, em conformidade com a LGPD.
          </span>
        </label>
        {errors.lgpd && <p className={errorClass}>{errors.lgpd}</p>}
      </div>
      <button type="submit" className="w-full flex items-center justify-center gap-2 bg-green-700 hover:bg-green-800 text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-green-500/30 transition-all transform hover:-translate-y-0.5 mt-2">
        <span>Enviar e Falar no WhatsApp</span>
        <WhatsAppIcon className="w-5 h-5" />
      </button>

      <p className="text-xs text-slate-500 text-center flex items-center justify-center gap-1">
        <Lock className="w-3 h-3" /> Seus dados estão seguros.
      </p>
    </form>
  );
}

interface SidebarFormProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SidebarForm({ isOpen, onClose }: SidebarFormProps) {
  const [sending, setSending] = useState(false);

  const handleSubmit = async (data: FormState) => {
    setSending(true);
    try {
      const { error } = await supabase.functions.invoke("send-contact-email", {
        body: { nome: data.nome.trim(), whatsapp: data.whatsapp, cnpj: data.cnpj, cidade: data.cidade.trim(), uf: data.uf },
      });
      if (error) throw error;
      toast.success("Contato enviado para nossos representantes! Em breve entraremos em contato.");
      onClose();
    } catch (err) {
      console.error("Erro ao enviar e-mail:", err);
      toast.error("Erro ao enviar. Tente novamente ou utilize outro canal de atendimento.");
    }
    setSending(false);
  };

  return (
    <>
      <div onClick={onClose} className={`fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300 ${isOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"}`} />
      <div className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-[70] shadow-2xl flex flex-col transition-transform duration-300 ease-in-out overflow-y-auto ${isOpen ? "translate-x-0" : "translate-x-full"}`}>
        <div className="bg-brand-blue p-6 flex justify-between items-start sticky top-0 z-10">
          <div>
            <h2 className="font-heading font-bold text-2xl text-white">Solicitar Contato</h2>
            <p className="text-blue-200 text-sm mt-1">Preencha para falar com o representante.</p>
          </div>
          <button aria-label="Fechar formulário" onClick={onClose} className="text-white/70 hover:text-white transition-colors ml-4 mt-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 p-6">
          <LeadForm onSubmit={handleSubmit} layout="sidebar" />
        </div>
      </div>
    </>
  );
}
